import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ArrowRight, Copy, FileJson, X } from 'lucide-react';
import tw, { styled } from 'twin.macro';

const Backdrop = styled.div`
  ${tw`fixed inset-0 z-50 flex items-end justify-center bg-zinc-950/65 p-0 sm:items-center sm:p-6`}
  animation: import-dialog-backdrop-in 160ms ease-out both;

  @keyframes import-dialog-backdrop-in {
    from {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Surface = styled.div`
  ${tw`flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl bg-white text-zinc-950 shadow-2xl sm:rounded-xl`}
  animation: import-dialog-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both;

  @keyframes import-dialog-in {
    from {
      opacity: 0;
      transform: translateY(0.75rem);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default function ImportStringDialog({
  copyStatus,
  hasPortfolio,
  importString,
  isMigrating,
  onClose,
  onCopy,
  onImport,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const canTransfer = hasPortfolio && typeof importString === 'string' && importString.length > 0;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  return createPortal(
    <Backdrop
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Surface
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-string-dialog-title"
        aria-describedby="import-string-dialog-description"
        onKeyDown={handleKeyDown}
      >
        <header tw="flex items-start justify-between gap-4 border-b border-zinc-200 px-4 py-4 sm:px-6">
          <div>
            <h2
              id="import-string-dialog-title"
              tw="flex items-center gap-2 text-xl font-semibold tracking-[-0.02em] sm:text-2xl"
            >
              <FileJson size={20} aria-hidden="true" tw="shrink-0 text-indigo-600" />
              Säkerhetskopia av din portfölj
            </h2>
            <p
              id="import-string-dialog-description"
              tw="mt-1 text-xs leading-5 text-zinc-600 sm:text-sm"
            >
              Spara strängen på ett tryggt ställe eller flytta innehaven direkt till den nya appen.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Stäng importsträngen"
            tw="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-700 transition hover:bg-zinc-200 hover:text-zinc-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <X size={19} aria-hidden="true" />
          </button>
        </header>

        <div tw="overflow-y-auto px-4 py-4 sm:px-6">
          {!hasPortfolio && (
            <div
              role="alert"
              tw="mb-4 flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm leading-6 text-amber-950"
            >
              <AlertTriangle size={19} aria-hidden="true" tw="mt-0.5 shrink-0 text-amber-700" />
              <p>
                <strong tw="block font-semibold">Inga aktier hittades på den här enheten.</strong>
                Vi hittar inga aktier sparade i den här webbläsaren. Prova den webbläsare eller
                enhet där du senast använde Aktieutdelningar.
              </p>
            </div>
          )}

          {hasPortfolio && !importString && (
            <div
              role="alert"
              tw="mb-4 flex gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm leading-6 text-red-950"
            >
              <AlertTriangle size={19} aria-hidden="true" tw="mt-0.5 shrink-0 text-red-700" />
              <p>
                <strong tw="block font-semibold">Importsträngen kunde inte läsas.</strong>
                Din portfölj visas fortfarande på sidan och har inte ändrats. Ladda om sidan och
                försök igen innan du kopierar eller flyttar den.
              </p>
            </div>
          )}

          <label htmlFor="manual-import-string" tw="text-sm font-semibold text-zinc-900">
            Importsträng
          </label>
          <textarea
            id="manual-import-string"
            value={importString || ''}
            readOnly
            spellCheck="false"
            tw="mt-2 h-44 w-full resize-none overflow-auto rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-3 font-mono text-xs leading-5 text-zinc-800 shadow-inner focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/30"
          />
          <p tw="mt-2 text-xs leading-5 text-zinc-500">
            Strängen innehåller din lokalt sparade portfölj. Dela den bara med en tjänst du litar
            på.
          </p>
        </div>

        <footer tw="border-t border-zinc-200 bg-zinc-50 px-4 py-4 sm:px-6">
          <div tw="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCopy}
              disabled={!canTransfer}
              tw="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-900 transition hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Copy size={17} aria-hidden="true" />
              Kopiera till urklipp
            </button>
            <button
              type="button"
              onClick={onImport}
              disabled={!canTransfer || isMigrating}
              tw="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isMigrating ? 'Förbereder flytten…' : 'Importera direkt till nya appen'}
              {!isMigrating && <ArrowRight size={17} aria-hidden="true" />}
            </button>
          </div>
          <p role="status" aria-live="polite" tw="mt-2 min-h-5 text-right text-xs text-zinc-600">
            {copyStatus}
          </p>
        </footer>
      </Surface>
    </Backdrop>,
    document.body
  );
}
